import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

/*
 * Migration
 * ─────────
 * Animated CLI progress bar showing a migration from your previous accounting software.
 * On mobile the animation is shorter (fewer log lines, faster tick)
 * and lands on a static "COMPLETE" end-state within ~4s.
 * Respects prefers-reduced-motion (skips animation).
 */

const STEPS_DESKTOP = [
    { label: "Reading backup archive",                    duration: 1400 },
    { label: "Importing chart of accounts",               duration: 1200 },
    { label: "Importing contacts · ~300 vendors + cust",  duration: 1600 },
    { label: "Importing items · ~80",                     duration: 800 },
    { label: "Importing invoices · ~1,200",               duration: 2200 },
    { label: "Importing bills · ~900",                    duration: 1800 },
    { label: "Importing payments · ~1,900",               duration: 2000 },
    { label: "Reconciling trial balance",                 duration: 1200 },
];

const STEPS_MOBILE = [
    { label: "Reading backup archive",       duration: 700 },
    { label: "Chart of accounts",            duration: 600 },
    { label: "Contacts · ~300",              duration: 700 },
    { label: "Invoices · ~1,200",            duration: 900 },
    { label: "Payments · ~1,900",            duration: 800 },
    { label: "Trial balance · reconciled",   duration: 500 },
];

function useIsMobile() {
    const [m, setM] = useState(() => (typeof window !== "undefined") && window.matchMedia("(max-width: 768px)").matches);
    useEffect(() => {
        if (typeof window === "undefined") return undefined;
        const mq = window.matchMedia("(max-width: 768px)");
        const onChange = () => setM(mq.matches);
        mq.addEventListener?.("change", onChange);
        return () => mq.removeEventListener?.("change", onChange);
    }, []);
    return m;
}

export default function Migration() {
    const navigate = useNavigate();
    const mobile = useIsMobile();
    const steps = mobile ? STEPS_MOBILE : STEPS_DESKTOP;
    const total = steps.reduce((a, s) => a + s.duration, 0);

    const [current, setCurrent] = useState(-1); // -1 = not started
    const [progress, setProgress] = useState(0);
    const ref = useRef(null);

    // Trigger when section is in view
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") { setCurrent(0); return undefined; }
        const prefersReduced = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
        if (prefersReduced) {
            setCurrent(steps.length);
            setProgress(100);
            return undefined;
        }
        const io = new IntersectionObserver(([e]) => {
            if (e.isIntersecting && current === -1) {
                setCurrent(0);
                io.disconnect();
            }
        }, { threshold: 0.3 });
        io.observe(el);
        return () => io.disconnect();
    }, []);

    // Advance through steps
    useEffect(() => {
        if (current < 0 || current >= steps.length) return undefined;
        const s = steps[current];
        const start = performance.now();
        let raf;
        const elapsedBefore = steps.slice(0, current).reduce((a, x) => a + x.duration, 0);
        const tick = (now) => {
            const p = Math.min(1, (now - start) / s.duration);
            const totalElapsed = elapsedBefore + p * s.duration;
            setProgress(Math.min(100, (totalElapsed / total) * 100));
            if (p < 1) {
                raf = requestAnimationFrame(tick);
            } else {
                setCurrent((c) => c + 1);
            }
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, [current]);

    const done = current >= steps.length;

    return (
        <section className="vy-section" id="migration" ref={ref} data-testid="landing-migration">
            <div className="vy-container">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1.05fr", gap: 48, alignItems: "center" }} className="vy-grid-2">
                    <div className="vy-reveal">
                        <div className="vy-kicker">&gt; migrate --from=old-books</div>
                        <h2 className="vy-serif vy-h-section" style={{ fontSize: "2.8rem", marginTop: 10, color: "var(--vy-navy)", lineHeight: 1.05 }}>
                            Bring 12 months of books over{" "}
                            <span className="vy-serif-i" style={{ color: "var(--vy-gold)" }}>in 5 minutes.</span>
                        </h2>
                        <p style={{ marginTop: 18, color: "var(--vy-muted)", fontSize: 15.5, lineHeight: 1.6, maxWidth: 500 }}>
                            Upload your previous accounting software&apos;s backup. Vyaahar reads every contact, item, journal,
                            invoice, bill and payment — and gives you a full reconciliation report on the other side.
                            Every rupee accounted for.
                        </p>
                        <ul style={{
                            listStyle: "none", padding: 0, marginTop: 22, display: "grid", gap: 8,
                            fontSize: 13, color: "var(--vy-navy)", fontFamily: "IBM Plex Mono, monospace",
                        }}>
                            <li>· contacts · items · CoA · journals</li>
                            <li>· invoices · bills · payments · credit notes</li>
                            <li>· reconciliation report with variance highlights</li>
                        </ul>
                        <div style={{ marginTop: 26, display: "flex", gap: 12, flexWrap: "wrap" }}>
                            <button type="button" className="vy-btn vy-btn-primary" onClick={() => navigate("/signup")} data-testid="migration-cta">
                                Start free · import your books
                            </button>
                        </div>
                    </div>

                    <div className="vy-reveal" data-testid="migration-terminal">
                        <div className="vy-terminal">
                            <div><span className="vy-t-prompt">$ </span><span className="vy-t-user">vyaahar migrate --from=your-old-books --file=backup.zip</span></div>
                            <div style={{ marginTop: 10 }}>
                                {steps.slice(0, Math.max(0, current + (done ? 0 : 1))).map((s, i) => {
                                    const isCurrent = i === current && !done;
                                    return (
                                        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "3px 0" }}>
                                            <span>
                                                <span className="vy-t-ok">{isCurrent ? "…" : "✓"}</span> {s.label}
                                            </span>
                                            {!isCurrent && <span className="vy-t-ok">OK</span>}
                                        </div>
                                    );
                                })}
                                <div className="vy-progress" aria-hidden>
                                    <div className="vy-progress-fill" style={{ width: `${progress}%` }} />
                                </div>
                                <div style={{ fontSize: 10.5, color: "rgba(246,242,235,0.55)", marginTop: 4 }}>
                                    {Math.floor(progress)}% · {done ? "complete" : "in progress"}
                                </div>
                                {done && (
                                    <div style={{ marginTop: 14, borderTop: "1px dashed rgba(166,138,86,0.35)", paddingTop: 10 }}>
                                        <div className="vy-t-ok">
                                            ✓ Reconciliation ties · trial balance balanced · zero variance.
                                        </div>
                                        <div style={{ marginTop: 4, color: "rgba(246,242,235,0.65)" }}>
                                            You&apos;re live in Vyaahar. Open dashboard? <span className="vy-t-warn">[Y]</span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <p
                            style={{
                                marginTop: 10, fontSize: 10.5, color: "var(--vy-muted)",
                                fontFamily: "IBM Plex Mono, monospace", letterSpacing: "0.02em",
                            }}
                            data-testid="migration-footnote"
                        >
                            * illustrative timings and counts. actual migration time depends on your data volume.
                        </p>
                    </div>
                </div>
            </div>
        </section>
    );
}
